import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { auth, db, deleteUser, doc, deleteDoc, collection, addDoc, OperationType, handleFirestoreError } from '../firebase';

interface TerminateAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TerminateAccountModal: React.FC<TerminateAccountModalProps> = ({ isOpen, onClose }) => {
  const [feedback, setFeedback] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleTerminate = async () => {
    const user = auth.currentUser;
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      // 1. Save feedback (optional, but requested by user)
      // We can save it to a global 'feedback' collection or just a 'terminations' collection
      if (feedback.trim()) {
        await addDoc(collection(db, 'terminations'), {
          uid: user.uid,
          email: user.email,
          feedback: feedback,
          timestamp: new Date().toISOString()
        });
      }

      // 2. Delete user data from Firestore (best effort)
      // Note: Deleting subcollections in Firestore from client is tricky without a recursive delete.
      // For this app, we'll delete the main profile.
      await deleteDoc(doc(db, 'users', user.uid));

      // 3. Delete the user from Firebase Auth
      // This "forgets" email and password
      await deleteUser(user);
      
      // Success - the user is gone.
      onClose();
      window.location.reload(); // Force reload to clear any local state
    } catch (err: any) {
      console.error('Termination failed:', err);
      if (err.code === 'auth/requires-recent-login') {
        setError('Security requires a recent login. Please sign out and sign in again before deleting your account.');
      } else {
        setError(err.message || 'Account deletion failed. Please contact support.');
      }
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div key="terminate-account-modal-wrapper" className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !loading && onClose()}
            className="absolute inset-0 bg-black/75"
          />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 15 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 15 }}
            className="relative w-full max-w-md bg-[#3a3430] border border-white/[0.06] rounded-xl overflow-hidden max-h-[calc(100vh-2rem)] md:max-h-[calc(100vh-4rem)] flex flex-col text-white"
          >
            <div className="p-6 space-y-6 flex-1 overflow-y-auto no-scrollbar bg-[#3a3430]">
              <div className="flex items-center gap-3.5">
                <div className="w-10 h-10 bg-red-500/10 border border-red-500/20 text-red-400 flex items-center justify-center rounded-lg">
                  <i className="fas fa-user-slash text-base"></i>
                </div>
                <div>
                  <h2 className="text-base font-bold uppercase tracking-tight font-heading text-white">Delete Account</h2>
                  <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-red-400 font-heading">Permanent Account Deletion</p>
                </div>
              </div>

              {!isConfirming ? (
                <div className="space-y-4">
                  <p className="text-xs font-medium text-white/70 leading-relaxed">
                    We're sorry to see you go. Before your account and data are permanently deleted, could you tell us why you're leaving?
                  </p>
                  
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-bold uppercase tracking-widest text-white/40 font-heading">Feedback (Optional)</label>
                    <textarea
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                      placeholder="Reason for deleting account..."
                      className="w-full h-28 p-3 bg-[#322D29] border border-white/[0.08] rounded-lg text-xs font-medium text-white placeholder-white/30 outline-none focus:border-red-500 transition-all resize-none"
                    />
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={onClose}
                      className="flex-1 py-3 text-[9px] font-bold uppercase tracking-widest text-white/60 hover:text-white transition-all font-heading cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => setIsConfirming(true)}
                      className="flex-1 py-3 bg-[#322D29] border border-white/10 hover:border-red-500/40 text-white hover:text-red-400 text-[9px] font-bold uppercase tracking-widest rounded-lg transition-all font-heading cursor-pointer"
                    >
                      Proceed
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-3.5 bg-red-950/30 border border-red-500/30 rounded-lg space-y-1.5">
                    <p className="text-[9px] font-bold uppercase tracking-widest text-red-400 font-heading">Warning: Irreversible Action</p>
                    <p className="text-[11px] font-medium text-red-200/90 leading-relaxed">
                      Confirming this action will immediately delete your email, password, and all associated venture data from our systems. This cannot be undone.
                    </p>
                  </div>

                  {error && (
                    <div className="p-3 bg-red-950/40 border border-red-500/40 text-red-300 text-[9px] font-bold uppercase tracking-widest rounded-lg">
                      {error}
                    </div>
                  )}

                  <div className="flex gap-3 pt-2">
                    <button
                      disabled={loading}
                      onClick={() => setIsConfirming(false)}
                      className="flex-1 py-3 text-[9px] font-bold uppercase tracking-widest text-white/60 hover:text-white transition-all disabled:opacity-50 font-heading cursor-pointer"
                    >
                      Go Back
                    </button>
                    <button
                      disabled={loading}
                      onClick={handleTerminate}
                      className="flex-1 py-3 bg-red-600 hover:bg-red-500 text-white text-[9px] font-bold uppercase tracking-widest rounded-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2 font-heading cursor-pointer"
                    >
                      {loading ? (
                        <>
                          <i className="fas fa-circle-notch animate-spin"></i>
                          Deleting...
                        </>
                      ) : (
                        'Delete Account'
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default TerminateAccountModal;
